
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { get, post } from '@/services/http';
import { StoreModel } from '@/models/store';
import { redirectExternal } from '@/utils/shopifyBridge';
import { Route } from 'vue-router';

interface ClientDetails {
  clientName: string;
}
@Component
export default class ConsentScreen extends Vue {
  @Prop({ default: () => new StoreModel() })
  public store!: StoreModel;
  @Prop() public app: any;

  public loading: boolean = true;
  public error: string | null = null;
  public clientId: string | null = null;
  public clientName: string = '';
  public scopes: string[] = ['profile:read', 'inventory:read', 'orders:read'];
  public redirectUri: string | null = null;
  public state: string | null = null;
  public codeChallenge: string | null = null;
  public codeChallengeMethod: string | null = null;
  public nonce: string | null = null;

  public created() {
    this.parseQueryParameters();
    if (!this.error) {
      this.fetchClientData();
    }
  }

  @Watch('$route.query', { immediate: false })
  public onRouteQueryChange() {
    // Re-parse if we previously had an error due to missing client_id
    // This handles the race condition where Vue Router updates after created()
    if (this.error && this.error.includes('client ID is missing')) {
      this.error = null;
      this.parseQueryParameters();
      if (!this.error) {
        this.fetchClientData();
      }
    }
  }

  public parseQueryParameters() {
    // Try Vue Router first, then fallback to window.location.search
    // This handles race conditions in Shopify embedded mode where
    // Vue Router may not have synced with the URL yet
    let client_id = this.$route.query.client_id as string;
    let redirect_uri = this.$route.query.redirect_uri as string;
    let scope = this.$route.query.scope as string;
    let state = this.$route.query.state as string;
    let code_challenge = this.$route.query.code_challenge as string;
    let code_challenge_method = this.$route.query.code_challenge_method as string;
    let nonce = this.$route.query.nonce as string;

    // Fallback to window.location.search if client_id is missing from Vue Router
    if (!client_id && window.location.search) {
      const urlParams = new URLSearchParams(window.location.search);
      client_id = urlParams.get('client_id') || '';
      redirect_uri = urlParams.get('redirect_uri') || '';
      scope = urlParams.get('scope') || '';
      state = urlParams.get('state') || '';
      code_challenge = urlParams.get('code_challenge') || '';
      code_challenge_method = urlParams.get('code_challenge_method') || '';
      nonce = urlParams.get('nonce') || '';
    }

    if (!client_id) {
      this.error = 'The client ID is missing from the request.';
      this.loading = false;
      return;
    }

    this.clientId = client_id as string;
    this.redirectUri = redirect_uri as string;
    this.scopes = scope ? (scope as string).split('assisty_space') : this.scopes;
    this.state = state as string;
    this.codeChallenge = code_challenge as string;
    this.codeChallengeMethod = code_challenge_method as string;
    this.nonce = nonce as string;
  }

  public async fetchClientData() {
    this.loading = true;
    try {
      const response: any = await get(`/oauth/clients/${this.clientId}/public`);
      this.clientName = response.clientName;
      this.error = null;
    } catch (e: any) {
      this.error = e.response?.data?.message || 'Could not find the application. Please ensure the client ID is correct and try again.';
      console.error(e);
    } finally {
      this.loading = false;
    }
  }

  public mockFetchClientDetails(clientId: string): Promise<ClientDetails> {
    return new Promise((resolve) => {
      setTimeout(() => {
        // Return mock data for any client ID to facilitate testing.
        resolve({
          clientName: 'Awesome Reporting App (Mocked)',
        });
      }, 800);
    });
  }

  public formatScope(scope: string): string {
    const descriptions: { [key: string]: string } = {
      'profile': 'View your basic profile information',
      'inventory': 'View your product and inventory data',
      'orders': 'View your sales orders',
      'email': 'View your email address',
      'default': `Perform action: ${scope}`
    };
    return descriptions[scope] || descriptions.default;
  }
  
  public getScopeIcon(scope: string): string {
      const icons: { [key: string]: string } = {
          read: 'mdi-eye-outline',
          write: 'mdi-pencil-outline',
          profile: 'mdi-account-circle-outline',
          'default': 'mdi-check-circle-outline'
      };
      const [type, action] = scope.split(':');
      if (icons[type]) return icons[type];
      if (icons[action]) return icons[action];
      return icons.default;
  }

  public getAvatarColor(name: string): string {
    if (!name) { return 'grey'; }
    const colors = [
      '#F44336', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5', '#2196F3',
      '#03A9F4', '#00BCD4', '#009688', '#4CAF50', '#8BC34A',
      '#FFC107', '#FF9800', '#FF5722', '#795548', '#607D8B'
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
      hash |= 0; // Ensure 32bit integer
    }
    return colors[Math.abs(hash) % colors.length];
  }

  public async allowAccess() {
    this.loading = true; // Show spinner during API call
    try {
      const response: any = await post('/oauth/authorize/code', {
        client_id: this.clientId,
        redirect_uri: this.redirectUri,
        scope: this.scopes.join(' '),
        state: this.state,
        nonce: this.nonce,
        code_challenge: this.codeChallenge,
        code_challenge_method: this.codeChallengeMethod,
      });

      if (response.redirectUri) {
        redirectExternal(response.redirectUri);
      } else {
        throw new Error('Redirect URI not provided in the response.');
      }
    } catch (e: any) {
      this.error = e.response?.data?.message || 'Failed to grant access. Please try again.';
      console.error(e);
      this.loading = false;
    }
  }

  public denyAccess() {
    const denyRedirectUri = `${this.redirectUri}?error=access_denied&state=${this.state}`;
    redirectExternal(denyRedirectUri);
  }
  
  public goBack() {
      window.history.back();
  }
}
